import React from 'react';
import { AlertTriangleIcon, ClockIcon, CreditCardIcon, Loader2Icon, MapPinIcon, PhoneIcon, StickyNoteIcon, UtensilsCrossedIcon, XIcon } from 'lucide-react';
import { cn, formatCurrency } from '../../../lib/utils';
import { useOrderDetail } from '../../../lib/order-detail-hooks';

// ──────────────────────────────────────────────────────────────────
// Detalle de un pedido web — Emilius Pizza.
//
// SOLO LECTURA a propósito: muestra qué pidió el cliente y qué hay que
// alistar, sin ningún botón que cambie el estado del pedido, lo anule
// ni lo reimprima. La cocina y la caja siguen siendo las dueñas de esos
// flujos; acá únicamente se consulta.
//
// Los montos se ocultan cuando `canSeeMoney` es false, igual que en el
// resto del módulo Express.
// ──────────────────────────────────────────────────────────────────

interface WebOrderDetailModalProps {
  orderId: string | null;
  isOpen: boolean;
  onClose: () => void;
  canSeeMoney: boolean;
}
const fmtCR = (iso: string | null | undefined) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-CR', {
      timeZone: 'America/Costa_Rica',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch {
    return iso;
  }
};
const deliveryLabel = (type: string | null | undefined) => {
  switch (type) {
    case 'delivery':
      return 'Envío a domicilio';
    case 'pickup':
      return 'Recoger en el local';
    case 'dine_in':
      return 'Comer en el local';
    default:
      return type || '—';
  }
};
const paymentLabel = (method: string | null | undefined) => {
  switch (method) {
    case 'cash':
      return 'Efectivo';
    case 'card':
      return 'Tarjeta';
    case 'sinpe':
      return 'SINPE Móvil';
    case 'online':
      return 'Pago en línea';
    default:
      return method || '—';
  }
};
const isCancelledItem = (status: string | null | undefined) => ['cancelled', 'canceled', 'removed', 'voided', 'deleted'].includes(String(status || '').toLowerCase());
export function WebOrderDetailModal({
  orderId,
  isOpen,
  onClose,
  canSeeMoney
}: WebOrderDetailModalProps) {
  const {
    order,
    loading,
    error
  } = useOrderDetail(isOpen ? orderId : null);
  if (!isOpen) return null;
  const mapsHref = order?.maps_url || (order?.delivery_lat && order?.delivery_lng ? `https://www.google.com/maps/search/?api=1&query=${order.delivery_lat},${order.delivery_lng}` : null);

  // Total de unidades a alistar, sin contar líneas anuladas.
  const totalUnits = (order?.items || []).filter(it => !isCancelledItem(it.status)).reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);
  return <div className="fixed inset-0 z-[130] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} role="dialog" aria-modal="true" aria-label="Detalle del pedido" data-id="element-906" data-mp="3nbi3or2lr5f5">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-907" data-mp="1lfaoorjccy93">
        {/* Encabezado */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3" data-id="element-908" data-mp="1bpze10gt3lj3">
          <div className="min-w-0" data-id="element-909" data-mp="17ono0tu0syad">
            <h3 className="font-black text-gray-900 truncate" data-id="element-910" data-mp="17l9nlr4x2z5k">
              {order ? `#${order.order_number || order.id}` : 'Pedido'}
            </h3>
            <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5" data-id="element-911" data-mp="1zflfjuknicu9">
              <ClockIcon className="w-3.5 h-3.5 shrink-0" data-id="element-912" data-mp="1vcfagq99yrpd" />
              {fmtCR(order?.created_at)}
            </p>
          </div>
          <button onClick={onClose} className="w-9 h-9 shrink-0 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500" aria-label="Cerrar" data-id="element-913" data-mp="1qd2eng0feaan">
            <XIcon className="w-5 h-5" data-id="element-914" data-mp="ex42h39yb857" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto" data-id="element-915" data-mp="22ubugromvsav">
          {loading ? <div className="flex justify-center py-16 text-gray-400" data-id="element-916" data-mp="27ltn8sgi6mkr">
              <Loader2Icon className="w-6 h-6 animate-spin" data-id="element-917" data-mp="27nxy5uctkluj" />
            </div> : error ? <div className="m-5 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2" data-id="element-918" data-mp="1tsvkgl1qds76">
              <AlertTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" data-id="element-919" data-mp="2llcs9ce0phsu" />
              <span data-id="element-920" data-mp="2lk1pnfwb6g3b">No se pudo cargar el detalle del pedido.</span>
            </div> : !order ? <div className="text-center py-16 text-sm text-gray-500" data-id="element-921" data-mp="2cl6gbuxywpvs">
              Pedido no encontrado.
            </div> : <>
              {/* Cliente y entrega */}
              <section className="px-5 py-4 border-b border-gray-100 space-y-2" data-id="element-922" data-mp="11610dp8tsuu8">
                <p className="text-sm font-bold text-gray-900" data-id="element-923" data-mp="1184399v2uigd">
                  {order.customer_name || 'Sin nombre'}
                </p>
                {order.customer_phone && <p className="text-sm text-gray-600 flex items-center gap-1.5 tabular-nums" data-id="element-924" data-mp="xmacasw1z98j">
                    <PhoneIcon className="w-3.5 h-3.5 shrink-0 text-gray-400" data-id="element-925" data-mp="tlywbtemxx3e" />
                    <a href={`tel:${order.customer_phone}`} className="underline hover:text-gray-900" data-id="element-926" data-mp="17l85wa8c5sye">
                      {order.customer_phone}
                    </a>
                  </p>}
                <div className="flex flex-wrap items-center gap-2 pt-1" data-id="element-927" data-mp="9z14f97bwgq9">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-700" data-id="element-928" data-mp="onf5pfp3jx4p">
                    {deliveryLabel(order.delivery_type)}
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-700 flex items-center gap-1" data-id="element-929" data-mp="jum430oxd4wu">
                    <CreditCardIcon className="w-3 h-3" data-id="element-930" data-mp="3ndp5ovj6tqu2" />
                    {paymentLabel(order.payment_method)}
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-50 text-blue-700" data-id="element-931" data-mp="1r9hjgwvmwnyu">
                    {order.status || '—'}
                  </span>
                </div>
                {order.delivery_address && <p className="text-sm text-gray-600 flex items-start gap-1.5 pt-1" data-id="element-932" data-mp="212geawhq5aqw">
                    <MapPinIcon className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400" data-id="element-933" data-mp="253s1ywh1flwg" />
                    {mapsHref ? <a href={mapsHref} target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-900 break-words" data-id="element-934" data-mp="1da8604ytc7yk">
                        {order.delivery_address}
                      </a> : <span className="break-words" data-id="element-935" data-mp="2sr097jy11514">
                        {order.delivery_address}
                      </span>}
                  </p>}
                {(order.delivery_zone || order.delivery_subzone) && <p className="text-xs text-gray-500 pl-5" data-id="element-936" data-mp="32ux6qvhy1vqd">
                    {[order.delivery_zone, order.delivery_subzone].filter(Boolean).join(' · ')}
                  </p>}
              </section>

              {/* Qué hay que alistar */}
              <section className="px-5 py-4" data-id="element-937" data-mp="2p4to0ei9wk5o">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-3" data-id="element-938" data-mp="2p1fm17diucyr">
                  <UtensilsCrossedIcon className="w-3.5 h-3.5" data-id="element-939" data-mp="2f5cko3qhuicv" />
                  Qué hay que alistar
                  <span className="ml-auto normal-case tracking-normal text-gray-400 tabular-nums" data-id="element-940" data-mp="2b4ajvigxhy8s">
                    {totalUnits} unidad{totalUnits === 1 ? '' : 'es'}
                  </span>
                </h4>

                {order.items.length === 0 ? <p className="text-sm text-gray-500" data-id="element-941" data-mp="9ajm8r7r6q8c">
                    Este pedido no tiene productos registrados.
                  </p> : <ul className="space-y-3" data-id="element-942" data-mp="wly0p90irkb0">
                    {order.items.map(item => {
                const cancelled = isCancelledItem(item.status);
                return <li key={item.id} className={cn('rounded-xl border border-gray-200 p-3', cancelled && 'opacity-50')} data-id="element-943" data-mp="ntnqvstjdgua">
                          <div className="flex items-start justify-between gap-3" data-id="element-944" data-mp="3w2wcyhbd9vre">
                            <p className="font-bold text-sm text-gray-900 min-w-0" data-id="element-945" data-mp="4vbdtvzi7mtf">
                              <span className="tabular-nums text-gray-500 mr-1.5" data-id="element-946" data-mp="1uark93m1mufm">
                                {item.quantity}×
                              </span>
                              {item.item_name_snapshot}
                              {cancelled && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700" data-id="element-947" data-mp="29boq6wqa8zz5">
                                  Anulado
                                </span>}
                            </p>
                            {canSeeMoney && <p className="text-sm font-bold text-gray-900 tabular-nums shrink-0" data-id="element-948" data-mp="2ddjsa3g1fhdz">
                                {formatCurrency(Number(item.line_total ?? 0) || Number(item.unit_price_snapshot || 0) * (Number(item.quantity) || 0))}
                              </p>}
                          </div>

                          {item.options && item.options.length > 0 && <ul className="mt-2 space-y-1 pl-4 border-l-2 border-gray-100" data-id="element-949" data-mp="12qx7kyzd5m4w">
                              {item.options.map(opt => <li key={opt.id} className="text-xs text-gray-600 flex items-baseline justify-between gap-2" data-id="element-950" data-mp="2qlmxyps4y4h2">
                                  <span className="break-words" data-id="element-951" data-mp="2go8udqrb9a66">
                                    {opt.option_group && <span className="text-gray-400" data-id="element-952" data-mp="3ea65v479wf8m">
                                        {opt.option_group}:{' '}
                                      </span>}
                                    {opt.option_name}
                                  </span>
                                  {canSeeMoney && Number(opt.price_delta) > 0 && <span className="tabular-nums text-gray-500 shrink-0" data-id="element-953" data-mp="3sb0ijz1ct2d3">
                                        +{formatCurrency(opt.price_delta)}
                                      </span>}
                                </li>)}
                            </ul>}

                          {item.notes && <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 flex items-start gap-1.5" data-id="element-954" data-mp="ie1ne6b6jwdw">
                              <StickyNoteIcon className="w-3.5 h-3.5 shrink-0 mt-0.5" data-id="element-955" data-mp="mdiut5a633lp" />
                              <span className="break-words" data-id="element-956" data-mp="rcn03ktz0h6r">{item.notes}</span>
                            </p>}
                        </li>;
              })}
                  </ul>}

                {order.notes && <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200" data-id="element-957" data-mp="164xdmyzzmjj1">
                    <p className="text-[11px] font-bold text-amber-900 uppercase tracking-wide mb-1" data-id="element-958" data-mp="1a5qwvf2hja5l">
                      Nota del cliente
                    </p>
                    <p className="text-sm text-amber-800 break-words" data-id="element-959" data-mp="3270oxvr85n4h">
                      {order.notes}
                    </p>
                  </div>}
              </section>

              {/* Totales — solo administración */}
              {canSeeMoney && <section className="px-5 py-4 border-t border-gray-100 space-y-1.5 bg-gray-50" data-id="element-960" data-mp="3pw33uvuh51va">
                  <Row label="Subtotal" value={formatCurrency(order.subtotal || 0)} data-id="element-961" data-mp="3kwyvu8jada6j" />
                  {Number(order.discount_amount) > 0 && <Row label="Descuento" value={`− ${formatCurrency(order.discount_amount || 0)}`} data-id="element-962" data-mp="3uvfqpglci8xv" />}
                  {order.delivery_type === 'delivery' && <Row label="Envío" value={formatCurrency(order.delivery_fee || 0)} data-id="element-963" data-mp="20ige7wc9huut" />}
                  <div className="flex items-center justify-between pt-1.5 border-t border-gray-200" data-id="element-964" data-mp="1uf1s549xjxll">
                    <span className="text-sm font-black text-gray-900" data-id="element-965" data-mp="1go6pi9q0ycqg">
                      Total
                    </span>
                    <span className="text-lg font-black text-gray-900 tabular-nums" data-id="element-966" data-mp="1lkx56vxx36bc">
                      {formatCurrency(order.total || 0)}
                    </span>
                  </div>
                </section>}
            </>}
        </div>
      </div>
    </div>;
}
function Row({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex items-center justify-between text-sm" data-id="element-967" data-mp="12muemcpq11bf">
      <span className="text-gray-600" data-id="element-968" data-mp="2s2ka95phec29">{label}</span>
      <span className="text-gray-900 tabular-nums font-medium" data-id="element-969" data-mp="2s3vboyvgf7q7">{value}</span>
    </div>;
}